import React, { useEffect, useState, useRef } from "react";
import Card from "components/card";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import MarketplaceService from "services/marketplace.service";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import { BsChevronDown } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";
import GooglePagination from "components/pagination/GooglePagination";

const columnHelper = createColumnHelper();

// Module-level tabCache for persistent data across component mounts/unmounts
const tabCache = {
  marketplace: {
    data: null,
    firstLoad: true,
  },
};

const Marketplace = () => {
  const dropdownRef = useRef();
  const [sorting, setSorting] = React.useState([]);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const getMarketplaceData = async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }
    try {
      const body = {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          filters: {
            status: "all"
          },
          sort: {
            createdAt: -1
          }
        },
        searchtext: searchText,
      };
      const response = await MarketplaceService.getMarketplaceList(body);
      const responseData = response.data.data;
      const responseTotalCount = response.data.totalCount || 0;
      
      // Update cache
      tabCache.marketplace.data = {
        data: responseData,
        totalCount: responseTotalCount,
      };
      
      // Set firstLoad to false after successful fetch
      if (tabCache.marketplace.firstLoad) {
        tabCache.marketplace.firstLoad = false;
      }
      
      setData(responseData);
      setTotalCount(responseTotalCount);
      setTotalPages(Math.ceil(responseTotalCount / pageSize));
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to fetch marketplace data");
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Check cache first
    const cache = tabCache.marketplace;
    
    // If cached data exists, show it instantly
    if (cache.data) {
      setData(cache.data.data || []);
      setTotalCount(cache.data.totalCount || 0);
      setTotalPages(Math.ceil((cache.data.totalCount || 0) / pageSize));
    }
    
    // Always fetch fresh data in background
    // Show loader only on firstLoad (when no cache exists yet)
    const shouldShowLoader = cache.firstLoad && !cache.data;
    getMarketplaceData(shouldShowLoader);
  }, [currentPage, pageSize, searchText]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusChange = async (rowId, newStatus) => {
    setIsViewLoading(true);
    try {
      const body = {
        verification_id: rowId,
        status: newStatus,
        adminNote: newStatus === "approved" 
          ? "Your marketplace verification has been approved. Welcome to our platform!" 
          : "Thank you for your interest in becoming a seller on our platform. After reviewing your application, we regret to inform you that it does not meet our current requirements for seller registration."
      };
      const response = await MarketplaceService.updateMarketplaceStatus(body);
      Toast("success", response.data.message);
      getMarketplaceData(false);
    } catch (error) {
      Toast(
        "error",
        error.response?.data?.message || "Failed to update status"
      );
    } finally {
      setActiveDropdown(null);
      setIsViewLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      approved: { color: "bg-green-500", text: "Approved" },
      pending: { color: "bg-yellow-500", text: "Pending" },
      rejected: { color: "bg-red-500", text: "Rejected" },
    };
    
    const config = statusConfig[status] || { color: "bg-gray-500", text: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const columns = [
    columnHelper.accessor("title", {
      id: "title",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Title</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">{info.getValue()}</p>
      ),
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => (
        <p className="text-sm font-bold uppercase text-white text-left">Description</p>
      ),
      cell: (info) => (
        <p 
          className="text-sm text-white break-words whitespace-normal leading-relaxed text-left line-clamp-2"
          title={info.getValue()}
        >
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("phoneNumber", {
      id: "phoneNumber",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Phone Number</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="text-sm font-bold text-white">{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor("account.name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Name</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() ? info.getValue() : "-"}
        </p>
      ),
    }),
    
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Status</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        const status = info.getValue();
        
        return (
          <div className="relative">
            <div
              className={`ms-center m-0 flex w-28 cursor-pointer items-center justify-between rounded-lg ${
                status === "approved" ? "bg-green-500/20" : 
                status === "rejected" ? "" : "bg-[#FF80001A]/10"
              } px-3 py-1.5 text-sm font-bold ${
                status === "approved" ? "text-green-500" :
                status === "rejected" ? "" : "text-[rgba(255,128,0,1)]"
              }`}
              style={status === "rejected" ? {
                backgroundColor: "rgba(255,37,37,0.1)",
                color: "#f06670d5"
              } : {}}
              onClick={() =>
                setActiveDropdown(activeDropdown === rowId ? null : rowId)
              }
            >
              <span>
                {status === "approved" ? "Approved" : 
                 status === "rejected" ? "Rejected" : "Pending"}
              </span>
              <BsChevronDown 
                className={`h-3 w-3 ${status === "approved" ? "text-green-500" : ""}`}
                style={status === "rejected" ? {
                  stroke: "#f06670d5",
                  strokeWidth: "1",
                  fill: "none"
                } : status === "pending" ? {
                  stroke: "rgba(255,128,0,1)",
                  strokeWidth: "1",
                  fill: "none"
                } : {
                  stroke: "currentColor",
                  strokeWidth: "1",
                  fill: "none"
                }}
              />
            </div>
            {activeDropdown === rowId && (
              <div
                className="absolute left-0 z-50 mt-1 w-28 rounded-md bg-[#0F1735] px-1 py-1"
                style={{ boxShadow: '0 0 10px 2px rgba(255, 255, 255, 0.15)' }}
                ref={dropdownRef}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="block w-full rounded-md px-2.5 py-1 text-center text-sm font-bold text-white transition-colors hover:bg-gray-500"
                  onClick={() => {
                    if (status !== "approved") {
                      handleStatusChange(rowId, "approved");
                    } else {
                      setActiveDropdown(null);
                    }
                  }}
                >
                  Approve
                </button>
                <div className="border-t border-gray-600 my-1 mx-2"></div>
                <button
                  className="block w-full rounded-md px-2.5 py-1 text-center text-sm font-bold text-white transition-colors hover:bg-gray-500"
                  onClick={() => {
                    if (status !== "rejected") {
                      handleStatusChange(rowId, "rejected");
                    } else {
                      setActiveDropdown(null);
                    }
                  }}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <div className="mt-3 grid h-full grid-cols-1">
      <div className="col-span-1 h-full w-full xl:col-span-1 min-w-0">
        <div className="flex justify-between gap-3 mb-4">
          <div className="mt-2 md:mt-4 flex h-fit justify-start">
            <div className="flex items-center rounded-full bg-navy-800 py-2 h-[40px] text-white xl:w-[170px]">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block w-[90px] max-w-[90px] bg-navy-800 text-sm font-medium text-white outline-none placeholder:!text-white"
                value={searchText}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
        <Card extra={"w-full h-fit pb-6 relative"}>
          <div className="mt-2 mx-2">
            <div className="overflow-x-auto !p-0 !m-0 !pr-0">
              <table className="table-fixed w-full min-w-[1000px] border-collapse !mr-0 !pr-0 !border-r-0">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="!border-px !border-gray-400">
                    {headerGroup.headers.map((header, index) => {
                      const isFirst = index === 0;
                      const isLast = index === headerGroup.headers.length - 1;
                      const isPhoneNumber = header.column.id === "phoneNumber";
                      const isDescription = header.column.id === "description";
                      const isTitle = header.column.id === "title";
                      const isName = header.column.id === "name";
                      const isStatus = header.column.id === "status";
                      
                      // Set fixed widths for each column
                      let widthClass = "";
                      if (isTitle) {
                        widthClass = "w-[130px]";
                      } else if (isDescription) {
                        widthClass = "w-[200px]";
                      } else if (isPhoneNumber) {
                        widthClass = "w-[200px]";
                      } else if (isName) {
                        widthClass = "w-[180px]";
                      } else if (isStatus) {
                        widthClass = "w-[140px]";
                      }
                      
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`${widthClass} cursor-pointer border-b-[1px] border-gray-500/10 pb-2 pt-4 text-start ${isFirst ? 'pl-6' : isPhoneNumber ? 'pl-24' : 'pl-4'} ${isLast ? '' : isDescription ? 'pr-1' : 'pr-4'}`}
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading && tabCache.marketplace?.firstLoad && !tabCache.marketplace?.data ? (
                  <tr>
                    <td colSpan={columns.length} className="h-[600px] align-middle">
                      <div className="flex h-full items-center justify-center">
                        <img
                          src={Loader}
                          alt="Loading..."
                          className="h-16 w-16"
                        />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows?.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center text-white">
                      No data found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, rowIndex) => {
                    const isLastRow = rowIndex === table.getRowModel().rows.length - 1;
                    return (
                      <tr key={row.id} className={isLastRow ? "" : "border-b-[1px] border-gray-500/10"}>
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const isFirst = cellIndex === 0;
                          const isLast = cellIndex === row.getVisibleCells().length - 1;
                          const isPhoneNumber = cell.column.id === "phoneNumber";
                          const isDescription = cell.column.id === "description";
                          const isTitle = cell.column.id === "title";
                          const isName = cell.column.id === "name";
                          const isStatus = cell.column.id === "status";
                          
                          // Set fixed widths for each column (matching header widths)
                          let widthClass = "";
                          if (isTitle) {
                            widthClass = "w-[130px]";
                          } else if (isDescription) {
                            widthClass = "w-[200px]";
                          } else if (isPhoneNumber) {
                            widthClass = "w-[200px]";
                          } else if (isName) {
                            widthClass = "w-[180px]";
                          } else if (isStatus) {
                            widthClass = "w-[140px]";
                          }
                          
                          return (
                            <td
                              key={cell.id}
                              className={`${widthClass} border-white/0 py-3 ${isFirst ? 'pl-6' : isPhoneNumber ? 'pl-24' : 'pl-4'} ${isLast ? '' : isDescription ? 'pr-1' : 'pr-4'}`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Google-style Pagination */}
          {!isLoading && table.getRowModel().rows.length > 0 && totalPages > 1 && (
            <div className="mt-3 pt-3 px-6">
              <GooglePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                className="w-full"
              />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Marketplace; 